import { NavLink } from "react-router-dom"
import { useRouteMatch } from "hooks/useRouteMatch.ts"
import Box from "@mui/material/Box"
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined"
import RuleIcon from "@mui/icons-material/Rule"
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined"
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import Avatar from "@mui/material/Avatar"
import { Collapse } from "@mui/material"
import React, { useContext } from "react"
import { FeatureToggle } from "App.tsx"
import { getLastSegmentForSidebarMenu } from "lib/utils.ts"

type TabType = {
    icon: React.JSX.Element
    label: string
    to: string
    toggle: boolean
    dataCy: string
}

export const SettingsMenu = ({ inSettings = false }) => {
    const featureContext = useContext(FeatureToggle)

    const menuTabs: TabType[] = [
        {
            icon: <PermIdentityOutlinedIcon />,
            label: "Gestion des conducteurs",
            to: "/settings/drivers",
            toggle: true,
            dataCy: "settings__side-bar--nav-drivers"
        },
        {
            icon: <LocalShippingOutlinedIcon />,
            label: "Gestion des véhicules",
            to: "/settings/vehicles",
            toggle: true,
            dataCy: "settings__side-bar--nav-vehicles"

        },
        {
            icon: <RuleIcon />,
            label: "Règles d’entreprise",
            to: "/settings/charging-rules",
            toggle: true,
            dataCy: "settings__side-bar--nav-drivers"

        },
        {
            icon: <AccountBalanceWalletOutlinedIcon />,
            label: "Gestion de l'entité",
            to: "/settings/entity",
            toggle: featureContext,
            dataCy: "settings__side-bar--nav-drivers"

        },
        {
            icon: <CreditCardIcon />,
            label: "Mode de paiement",
            to: "/settings/payment",
            toggle: featureContext,
            dataCy: "settings__side-bar--nav-drivers"

        },
    ]

    const links = menuTabs.map((tab) => tab.to)
    const currentRoute = useRouteMatch(links)

    return (
        <>
            <Collapse
                orientation="horizontal"
                in={inSettings}
                easing="ease-in-out"
                sx={{
                    height: "100%",
                }}
                data-testid="settings-menu-collapse"
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "252px",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        filter: "drop-shadow(0px 0px 50px rgba(0, 0, 0, 0.05))",
                    }}
                >
                    <GapMenu topElement />
                    <IndicatorCurrentTab
                        positionCurrentTab={links.findIndex(
                            (link) => link === currentRoute
                        )}
                    />
                    <MenuItems tabs={menuTabs} />
                    <GapMenu />
                </Box>
            </Collapse>
            {/*<ToggleButtonSettings inSettings={inSettings} displayMenu={displayMenu} handleChange={handleChange}/>*/}
        </>
    )
}

const LinkItem = ({ tab }: { tab: TabType }) => {
    const { to, icon, label } = tab
    return (
        <NavLink
            to={to}
            style={{
                textDecoration: "none",
                color: "#161616",
            }}
        >
            {({ isActive }) => (
                <Box
                    data-cy={`settings__side-bar--nav-${getLastSegmentForSidebarMenu(to)}`}
                    sx={{
                        width: "100%",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        ml: "25px",
                        typography: isActive ? "labelLarge" : "body",
                        transition: "all 0.3s ease-in-out",
                        ...(!isActive && {
                            "&:hover": {
                                "& .MuiAvatar-root": {
                                    color: "common.white",
                                    bgcolor: "secondary.main",
                                    mr: "12px",
                                },
                            },
                        }),
                    }}
                >
                    <Avatar
                        alt={label}
                        sx={{
                            transition: "all 0.3s ease-in-out",
                            color: !isActive
                                ? "secondary.main"
                                : "common.white",
                            bgcolor: !isActive
                                ? "transparent"
                                : "secondary.main",
                            height: "44px",
                            width: "44px",
                            mr: isActive ? "12px" : "2px",
                        }}
                    >
                        {icon}
                    </Avatar>
                    {label}
                </Box>
            )}
        </NavLink>
    )
}
const MenuItems = ({ tabs }: { tabs: TabType[] }) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                width: "100%",
                py: "50px",
            }}
        >
            {tabs.map((tab) =>
                tab.toggle ? <LinkItem key={tab.to} tab={tab} /> : null
            )}
        </Box>
    )
}
const GapMenu = ({ topElement = false }) => {
    const sxTopElement = {
        height: "130px",
    }
    const sxBottomElement = {
        flex: 1,
    }
    return (
        <Box
            sx={{
                width: "100%",
                bgcolor: "settings.main",
                ...(topElement ? sxTopElement : sxBottomElement),
            }}
        />
    )
}

const IndicatorCurrentTab = ({ positionCurrentTab = 0 }) => {
    positionCurrentTab = positionCurrentTab >= 0 ? positionCurrentTab : 0
    return (
        <Box
            sx={{
                position: "absolute",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                zIndex: -1,
                top: "calc(-50% + 130px + 50px + 20px)",
                left: 0,
                transition: "transform 0.3s ease-in-out",
                transform: `translateY(${positionCurrentTab * 60}px)`,
            }}
        >
            <RoundedCornerElement topElement />
            <IndicatorComponent />
            <RoundedCornerElement />
        </Box>
    )
}

const IndicatorComponent = () => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "60px",
            }}
        >
            <GapIndicator />
            <RoundedIndicator />
        </Box>
    )
}

const RoundedIndicator = () => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                color: "settings.main",
                boxShadow: "-30px 0 0 0",
                borderRadius: "100vh 0 0 100vh",
            }}
        />
    )
}
const GapIndicator = () => {
    return (
        <Box
            sx={{
                width: "18px",
                height: "100%",
                bgcolor: "settings.main",
                zIndex: 1,
            }}
        />
    )
}
const RoundedCornerElement = ({ topElement = false }) => {
    const sxTopElement = {
        borderRadius: "0 0 20px 0",
    }
    const sxBottomElement = {
        borderRadius: "0 20px 0 0",
    }
    return (
        <Box
            sx={{
                flex: 1,
                bgcolor: "settings.main",
                width: "100%",
                zIndex: 1,
                ...(topElement ? sxTopElement : sxBottomElement),
            }}
        ></Box>
    )
}

// const ToggleButtonSettings = ({inSettings, displayMenu, handleChange}) => {
//     return (
//         <>
//             {
//                 inSettings && (
//                     <ToggleButton
//                         onChange={handleChange}
//                         disableRipple
//                         sx={{
//                             position: "absolute",
//                             top: "50%",
//                             left: 2,
//                             zIndex: 2,
//                             transform: "translateY(-50%) rotate(90deg)",
//                             width: "10px",
//                             color: "grey.400",
//                             p: "0px",
//                             m: "0px",
//                             transition: "color 0.1s ease-in-out",
//                             ...(displayMenu && {}),
//                             '&.MuiToggleButton-root': {
//
//                                 bgcolor: "transparent",
//                                 border: "none",
//                                 '&:hover': {
//                                     bgcolor: "transparent",
//                                     color: "grey.600",
//                                 },
//                             },
//                         }}
//                     >
//                         <HorizontalRuleRoundedIcon/>
//                     </ToggleButton>
//                 )
//             }
//         </>
//     )
// }